import { OrganizationUnitCooperationSelection as OrganizationUnitCooperationSelectionType } from '../../../../../../types/Survey/Element'
import {
  OrganizationUnitCooperationSelection as OrganizationUnitCooperationSelectionAnalysisType,
  Type,
  Unit,
} from '../../../../../../types/Survey/Analysis/Element'
import { Props } from './types'
import Core from './Core'
import Pie, { Item as PieItem } from '../../../../../UI/Chart/Pie/Pie'
import Bar, { Item as BarItem } from '../../../../../UI/Chart/Bar/Bar'
import Relationship, { Node as RelationshipNode, Edge as RelationshipEdge } from '../../../../../UI/Chart/Relationship/Relationship'
import { ChartType } from './ChartSelector'
import { useEffect, useState } from 'react'
import { Survey } from '../../../../../../types/Survey/Survey'
import { OrganizationUnit } from '../../../../../../types/OrganizationUnit'
import { Analysis } from '../../../../../../types/Survey/Analysis/Analysis'
import useListSurveyAnalysis from '../../../../../hooks/useListSurveyAnalysis'

const OrganizationUnitCooperationSelection: React.FC<
  Props<OrganizationUnitCooperationSelectionType, OrganizationUnitCooperationSelectionAnalysisType> & { survey: Survey; analysis: Analysis }
> = ({ survey, analysis, element, analysisElement }) => {
  const [activeChart, setActiveChart] = useState(ChartType.BAR)
  const [analysisList, pull] = useListSurveyAnalysis(survey.uuid)

  // evaluate what analysis data sets for the relation chart are required, build the query and fetch
  useEffect(() => {
    if (!analysis.organizationUnitUUID && !analysis.dayNumber) {
      pull({ $exists: true }, { $exists: false })
    } else if (!!analysis.organizationUnitUUID && !analysis.dayNumber) {
      pull(analysis.organizationUnitUUID, { $exists: false })
    } else if (!analysis.organizationUnitUUID && !!analysis.dayNumber) {
      pull({ $exists: true }, analysis.dayNumber)
    } else {
      pull(analysis.organizationUnitUUID, analysis.dayNumber)
    }
  }, [analysis, pull])

  // building bar chart items
  const getBarChartItems = (): BarItem[] => {
    const items = survey.context.organizationUnitList.map((u: OrganizationUnit) => {
      const analysisOption: Unit = analysisElement.units.find((aU) => aU.uuid === u.uuid)
      return {
        key: u.uuid,
        value: analysisOption.percentage,
        label: `${u.name}`,
      }
    })

    const totalValues = items.reduce((acc, i) => acc + i.value, 0)
    return items.map((i) => ({ ...i, value: (i.value / totalValues) * 100 }))
  }

  // building pie chart items
  const getPieChartItems = (): PieItem[] => {
    const items = survey.context.organizationUnitList.map((u: OrganizationUnit) => {
      const analysisOption: Unit = analysisElement.units.find((aU) => aU.uuid === u.uuid)
      return {
        value: analysisOption.percentage,
        label: `${u.name}`,
      }
    })

    const totalValues = items.reduce((acc, i) => acc + i.value, 0)
    return items.map((i) => ({ ...i, value: (i.value / totalValues) * 100 }))
  }

  // building relationship chart nodes
  const getRelationshipChartNodes = (): RelationshipNode[] => {
    return survey.context.organizationUnitList.map((u) => ({ id: u.uuid, label: u.name }))
  }
  // building relationship chart edges
  const getRelationshipChartEdges = (): RelationshipEdge[] => {
    const edges: RelationshipEdge[] = []
    for (const a of analysisList) {
      for (const e of a.elements) {
        if (e.type === Type.ORGANIZATION_UNIT_COOPERATION_SELECTION) {
          for (const u of e.units) {
            edges.push({ source: a.organizationUnitUUID, target: u.uuid, weight: u.answerCount })
          }
        }
      }
    }

    return edges
  }

  if (!analysisList) return null
  return (
    <div>
      <Core
        element={element}
        availableChartOptions={[ChartType.PIE, ChartType.BAR, ChartType.RELATIONSHIP]}
        activeChart={activeChart}
        setActiveChart={setActiveChart}
      />
      <div className="mt-5 flex justify-center">
        {activeChart === ChartType.PIE && (
          <div className="w-full">
            <Pie items={getPieChartItems()} enableDownload />
          </div>
        )}
        {activeChart === ChartType.BAR && <Bar enableDownload items={getBarChartItems()} />}
        {activeChart === ChartType.RELATIONSHIP && <Relationship nodes={getRelationshipChartNodes()} edges={getRelationshipChartEdges()} />}
      </div>
    </div>
  )
}

export default OrganizationUnitCooperationSelection
